<template>
<w-main-template>
  <w-target-tree slot="sideContent" />
  <w-main-title slot="mainTitle">
    <div slot="title">
      <span v-show="isRoot && showFlowAnalysis" @click.prevent="setShowFlowAnalysis(false)"><i style="font-size:20px;cursor:pointer;" class="fa fa-arrow-left"></i></span>
      <div class="vertical-center-box">
        <div class="title-h2" style="margin=left:5px;margin-bottom:0px;">{{title}}</div>
        <div v-show="subTitle !== null" class="info-text text-medium" style="display:inline-block;margin-left:10px;">
          {{subTitle}}
        </div>
      </div>
    </div>
    <div slot="rightTool">
      <w-manage-tool-button-set v-show="isObj" :obj="selectedNode.obj" />
      <w-datetime-range-button v-show="showDatetimeRangeButton" :is-flow-analysis="isFlowAnalysis" />
      <w-report-button/>
    </div>
  </w-main-title>
  <component slot="mainContent" :is="currentView" :isFlowRole="isFlowRole" :selected-node="selectedNode"></component>
</w-main-template>
</template>

<script>
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import WMainTemplate from '@/components/template/MainTemplate';
import WMainTitle from '@/components/template/MainTitle';
import WManageToolButtonSet from '@/components/button/ManageToolButtonSet';
import WDatetimeRangeButton from '@/components/button/DatetimeRangeButton';
import WReportButton from '@/components/button/ReportButton';
import WTargetTree from '@/components/tree/TargetTree';
import { isRequireIPObjType } from '@/utils/tree';
import { rootNode } from '@/constants/tree';
import { objType } from '@/constants/obj';
import { menuDefine } from '@/constants/menu';
import { performanceNodeTabs } from '@/constants/performance';
import WVirtualPerformanceGroup from '@/views/virtual/performance/group';
import isNil from 'lodash/isNil';

import WPerformanceRoot from './root';
import WPerformanceGroup from './group';
import WPerformanceNode from './node';
import WFlowAnalysis from './FlowAnalysis';

export default {
  name: 'WPerformance',
  components: {
    WMainTemplate,
    WMainTitle,
    WTargetTree,
    WPerformanceRoot,
    WVirtualPerformanceGroup,
    WPerformanceGroup,
    WPerformanceNode,
    WFlowAnalysis,
    WDatetimeRangeButton,
    WManageToolButtonSet,
    WReportButton,
  },
  data() {
    return {
      /**
       * 현재 화면 컴포넌트명
       * @type {String}
       */
      currentView: null,
      /**
       * Flow 분석 화면 보기 여부
       * @type {Boolean}
       */
      showFlowAnalysis: false,
    };
  },
  computed: {
    /**
     * 트리에서 선택된 노드 객체
     * @author Ju
     * @return {Object} [description]
     */
    selectedNode() {
      return this.$store.getters.selectedNode;
    },
    /**
     * 타이틀
     * Flow 분석을 제외하고는 트리에 선택된 노드 라벨을 제공.
     * @author Ju
     * @return {String} [description]
     */
    title() {
      let title;
      if (this.showFlowAnalysis && this.isRoot) {
        title = 'Flow 분석';
      } else {
        title = this.selectedNode.label;
      }
      return title;
    },
    /**
     * 서브 타이틀 (IP/URL 정보 표현)
     * @author Ju
     * @return {String}
     */
    subTitle() {
      let title = null;
      const node = this.selectedNode;
      if (!this.showFlowAnalysis && node.type === 'obj') {
        if (isRequireIPObjType(node) && (!isNil(node.ipaddress) || !isNil(node.parentIpaddress))) {
          title = isNil(node.ipaddress) ? node.parentIpaddress : node.ipaddress;
        } else if (node.subType === 'e2e' && !isNil(node.httpUrl)) {
          title = node.httpUrl;
        }
      }
      return title;
    },
    /**
     * 선택된 노드 타입이 Obj인지 여부
     * @author Ju
     * @return {Boolean} [description]
     */
    isObj() {
      return this.$store.getters.isObj;
    },
    /**
     * 선택된 노드 타입이 Root인지 여부
     * @author Ju
     * @return {Boolean} [description]
     */
    isRoot() {
      if (!isEmpty(this.selectedNode) && rootNode.treeKey.indexOf(this.selectedNode.treeKey) >= 0) {
        return true;
      }
      return false;
    },
    /**
     * Root > Flow분석 || Node > Flow 분석 탭 선택시 기간필터 정보가 달라진다.
     * @return {Boolean} [description]
     * @author jda
     */
    isFlowAnalysis() {
      const nodeType = this.selectedNode.subType;
      const activeTab = this.activeTab;
      const isObjFlowAnalysis = (nodeType === objType.NETDEVICE || nodeType === objType.NETINTERFACE) && (!isEmpty(activeTab) && activeTab.name === 'flow-analysis');

      return isObjFlowAnalysis || this.showFlowAnalysis;
    },
    /**
     * Flow분석 권한 체크
     * @return {Boolean} [description]
     * @author jda
     */
    isFlowRole() {
      return this.isRoot && this.activeManagetypeIds.indexOf('NMS') >= 0 && !this.showFlowAnalysis && this.roles.some((role) => role === 'FLOW_ADMIN' || role === 'ADMIN');
    },
    isRootFlowAnalysis() {
      return this.isRoot && this.showFlowAnalysis;
    },
    /**
     * 기간 조건 버튼 제공 여부
     * @author Ju
     * @return {[type]} [description]
     */
    showDatetimeRangeButton() {
      return (!isEmpty(this.activeTab) && !isNil(this.activeTab) && this.activeTab.name === 'flow') || (this.isObj && (!isEmpty(this.activeTab) && this.activeTab.name !== performanceNodeTabs.config.name
        && this.activeTab.name !== performanceNodeTabs.summary.name
        && this.activeTab.name !== performanceNodeTabs.listview.name && this.activeTab.name !== performanceNodeTabs.virtualmap.name && this.activeTab.name !== performanceNodeTabs.transaction.name
        && this.activeTab.name !== performanceNodeTabs.operation.name));
    },
    treeMenu() {
      return this.$store.state.router.treeMenu;
    },
    ...mapGetters(['roles', 'activeTab', 'activeManagetypeIds']),
  },
  watch: {
    /**
     * 선택된 노드가 변경될 때 조건에 맞는 화면 컴포넌트 설정
     */
    selectedNode: {
      immediate: true,
      handler(to, from) {
        if (this.treeMenu === menuDefine.PERF_OPER && (this.currentView === null || (to.treeKey !== from.treeKey))) {
          this.changeView();
        }
      },
    },
  },
  created() {
    // this.$store.dispatch('setInitialDatetimeRange', { type: 'interval', interval: 'hour', value: 12 });
    this.$store.dispatch('setPlotLineDatetime', null);
  },
  methods: {
    /**
     * 선택된 노드가 변경될 때 조건에 맞는 화면 컴포넌트 설정한다.
     * @author Ju
     */
    changeView() {
      // this.showFlowAnalysis = false;
      if (this.isRoot) {
        // TODO: 선택된 treeKey가 없는 경우에 대한 예외 처리 필요.
        this.currentView = WPerformanceRoot.name;
      } else if (this.selectedNode.type === 'group') {
        this.showFlowAnalysis = false;
        if (this.selectedNode.subType === 'virtual') {
          this.currentView = WVirtualPerformanceGroup.name;
        } else {
          this.currentView = WPerformanceGroup.name;
        }
      } else if (this.selectedNode.type === 'obj') {
        this.showFlowAnalysis = false;
        this.currentView = WPerformanceNode.name;
      }
    },
    /**
     * Flow 분석 화면 보기 여부를 설정한다.
     * @author Ju
     * @param  {Boolean} showFlowAnalysis Flow 분석 화면 보기 여부
     */
    setShowFlowAnalysis(showFlowAnalysis) {
      this.showFlowAnalysis = showFlowAnalysis;
      this.currentView = showFlowAnalysis ? WFlowAnalysis.name : WPerformanceRoot.name;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/index.vue